import { CheckSquare, Search, Square } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { TailSpin } from "react-loader-spinner";
import { ADD_PERM } from "../../Utils/constant";
import { store } from "../../services/service";
import { usePermStore } from "../../store/PermissionStore";
import { useRoleStore } from "../../store/RoleStore";

export const RolePermission = () => {
  const { register, handleSubmit, reset, setValue, watch } = useForm();
  const [isSubmit, setIsSubmit] = useState(false);
  const [role, setRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedActions, setSelectedActions] = useState(new Set());
  const menuList = usePermStore();
  const roleListe = useRoleStore();

  // Filtrer les menus en fonction de la recherche
  const filteredMenus = menuList?.data?.filter((menu) => {
    const menuMatch = menu?.sousMenu?.description
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const actionMatch = menu?.actions?.some((action) =>
      action?.description?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    return menuMatch || actionMatch;
  });

  // Initialiser les actions sélectionnées
  useEffect(() => {
    if (menuList?.data?.length > 0) {
      const initialSelected = new Set();
      menuList.data.forEach((menu) => {
        menu.actions?.forEach((action) => {
          if (action?.isAssocie) {
            initialSelected.add(action.id);
          }
        });
      });
      setSelectedActions(initialSelected);
    }
  }, [menuList?.data]);

  // Gérer la sélection/désélection d'une action
  const toggleAction = (actionId) => {
    setSelectedActions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(actionId)) {
        newSet.delete(actionId);
      } else {
        newSet.add(actionId);
      }
      return newSet;
    });
  };

  // Tout sélectionner
  const selectAll = () => {
    const allActions = new Set();
    filteredMenus?.forEach((menu) => {
      menu.actions?.forEach((action) => {
        allActions.add(action.id);
      });
    });
    setSelectedActions(allActions);
  };

  // Tout désélectionner
  const deselectAll = () => {
    setSelectedActions(new Set());
  };

  // Sélectionner/désélectionner un groupe
  const toggleGroup = (menuIndex) => {
    const menu = filteredMenus[menuIndex];
    const groupActions = menu.actions?.map((a) => a.id) || [];
    const allSelected = groupActions.every((id) => selectedActions.has(id));

    setSelectedActions((prev) => {
      const newSet = new Set(prev);
      if (allSelected) {
        groupActions.forEach((id) => newSet.delete(id));
      } else {
        groupActions.forEach((id) => newSet.add(id));
      }
      return newSet;
    });
  };

  // Vérifier si un groupe est complètement sélectionné
  const isGroupSelected = (menu) => {
    return menu.actions?.every((action) => selectedActions.has(action.id));
  };

  // Vérifier si un groupe est partiellement sélectionné
  const isGroupPartial = (menu) => {
    const hasSelected = menu.actions?.some((action) =>
      selectedActions.has(action.id),
    );
    const hasUnselected = menu.actions?.some(
      (action) => !selectedActions.has(action.id),
    );
    return hasSelected && hasUnselected;
  };

  const submit = (data) => {
    const newData = [];
    const remove = [];

    // Actions actuellement sélectionnées
    const currentSelected = Array.from(selectedActions);

    // Actions initialement associées
    const initialAssociated = [];
    menuList.data?.forEach((menu) => {
      menu.actions?.forEach((action) => {
        if (action?.isAssocie) {
          initialAssociated.push(action.id);
        }
      });
    });

    // Actions à ajouter (sélectionnées mais non initialement associées)
    currentSelected.forEach((id) => {
      if (!initialAssociated.includes(id)) {
        newData.push(id);
      }
    });

    // Actions à retirer (initialement associées mais non sélectionnées)
    initialAssociated.forEach((id) => {
      if (!currentSelected.includes(id)) {
        remove.push(id);
      }
    });

    const submitData = {
      role: data.role,
      listActionId: newData,
      listActionRetireId: remove,
    };

    setIsSubmit(true);

    store(ADD_PERM, "application/json", submitData)
      .then((res) => {
        setIsSubmit(false);
        if (res.status === 200) {
          reset();
          menuList.getData(encodeURIComponent(role));
          toast.success("Enregistrement éffectué");
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        toast.error(err.response.data.donnee);
      });
  };

  useEffect(() => {
    roleListe.getData();
  }, []);

  useEffect(() => {
    reset();
    setSelectedActions(new Set());
    setSearchTerm("");
    menuList.getData(encodeURIComponent(role));
  }, [role]);

  return (
    <form onSubmit={handleSubmit(submit)} className="px-2">
      <div className="my-8">
        <div className="items-center">
          <div className="mb-3">
            <p className="font-medium">Profil utilisateur</p>
            <p className="text-xs">
              Selectionner un profil pour lequel on veut attribuer des
              permissions
            </p>
          </div>
          <hr />
          <label htmlFor="" className="text-sm font-medium">
            Role
          </label>
          <div className="font-mtn mb-8">
            <select
              value={role}
              {...register("role")}
              onChange={(e) => {
                setRole(e.target.value);
              }}
              className="capitalize select select-bordered w-full lg:w-96 h-10"
            >
              <option selected>Selectionnez un role</option>
              {roleListe?.data?.map((item) => (
                <option key={item?.id} value={item?.id}>
                  {item?.id
                    .replace(/ROLE_/g, " ")
                    .replace(/_/g, " ")
                    .toLowerCase()}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-3">
          <div className="flex gap-3 font-medium items-center flex-wrap">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="btn btn-xs h-9 bg-green-500 hover:bg-green-600 text-white border-none"
              >
                Tout sélectionner
              </button>
              <button
                type="button"
                onClick={deselectAll}
                className="btn btn-xs h-9 bg-red-500 hover:bg-red-600 text-white border-none"
              >
                Tout désélectionner
              </button>
            </div>
          </div>
        </div>

        {/* Barre de recherche */}
        {menuList?.data?.length > 0 && (
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher une action ou un menu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input input-bordered w-full pl-10 h-10"
              />
            </div>
            {searchTerm && (
              <p className="text-xs text-gray-500 mt-1">
                {filteredMenus?.length || 0} menu(s) trouvé(s)
              </p>
            )}
          </div>
        )}

        <hr />

        {menuList?.data?.length !== 0 && menuList.loading === false ? (
          <div className="my-3">
            {filteredMenus?.length > 0 ? (
              <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredMenus.map((item, index) => {
                  const groupSelected = isGroupSelected(item);
                  const groupPartial = isGroupPartial(item);

                  return (
                    <div
                      key={index}
                      className="border rounded-lg p-3 bg-white shadow-sm"
                    >
                      <div className="flex items-center justify-between mb-2 bg-slate-100 p-2 rounded">
                        <p className="text-sm font-medium">
                          {item?.sousMenu?.description}
                        </p>
                        <button
                          type="button"
                          onClick={() => toggleGroup(index)}
                          className="btn btn-xs gap-1"
                          title={
                            groupSelected
                              ? "Tout désélectionner"
                              : "Tout sélectionner"
                          }
                        >
                          {groupSelected ? (
                            <CheckSquare className="w-4 h-4" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                          {groupPartial && !groupSelected && (
                            <span className="text-xs">Partiel</span>
                          )}
                        </button>
                      </div>
                      <ul className="space-y-1">
                        {item?.actions?.map((action) => (
                          <li
                            key={action.id}
                            className="flex items-center gap-2 hover:bg-gray-50 p-1 rounded"
                          >
                            <input
                              type="checkbox"
                              checked={selectedActions.has(action.id)}
                              onChange={() => toggleAction(action.id)}
                              className="checkbox checkbox-sm checkbox-warning"
                            />
                            <p className="text-xs text-gray-600 flex-1">
                              {action?.description}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Aucun résultat trouvé pour "{searchTerm}"</p>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full my-4 flex items-center justify-center">
            <TailSpin
              height="40"
              width="40"
              color="#000"
              ariaLabel="tail-spin-loading"
              radius="2"
              wrapperStyle={{}}
              wrapperClass=""
              visible={menuList.loading}
            />
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmit}
          className={`${
            !isSubmit ? "text-black bg-main" : "text-slate-800 bg-slate-200"
          } btn mt-4`}
        >
          {!isSubmit ? "Enregistrer" : "Veuillez patientez..."}
          {isSubmit && (
            <span className="loading loading-dots loading-xs"></span>
          )}
        </button>
      </div>
    </form>
  );
};
